/**
 * Core orchestration hook for the Schedule Interview wizard.
 *
 * Three pre-fill paths — all handled by useWizardInitialValues:
 *
 *   1. Adhoc (adhoc / self-schedule-adhoc)
 *      No API. payloadRef seeded with ADHOC_PAYLOAD_DEFAULTS.
 *
 *   2. Create (manual / self-schedule)
 *      GET /ats_interviews/new → hydratePlanDefaults → payloadRef (all steps).
 *
 *   3. Edit (any mode)
 *      GET /ats_interviews/:id or /ats_schedules/:id
 *      → hydratePayloadFromApi → payloadRef (all steps).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEditConfirmation, useScorecardStopConfirm } from './editMode';
import type { TabItem } from '@kula-ai/components';
import { useStepValidation } from '@/hooks/useStepValidation';
import type {
  ScheduleMode,
  ApiReference,
  ScheduleInterviewPayload,
  ScheduleModeConfig,
} from './config/types';
import type { BulkSelfScheduleContextIds } from './payload/types';
import { isSelfScheduleMode } from './config/modeClassifiers';
import { MODE_CONFIGS } from './config/modes';
import { SCHEDULE_INTERVIEW_COPY } from './config/copy';
import { useWizardInitialValues } from './initialValues/useWizardInitialValues';
import { useStepRefs } from './hooks/useStepRefs';
import { useScheduleSave, type PhoneValue } from './hooks/useScheduleSave';

interface UseScheduleInterviewOptions {
  mode: ScheduleMode;
  apiReference: ApiReference;
  applicationId?: number;
  candidateId?: number;
  jobId?: number;
  stageId?: number;
  stageActivityId?: number;
  interviewId?: number;
  scheduleId?: number;
  /**
   * Candidate's current phone-on-record (from applicationData.contact).
   * Used to detect whether the user edited the phone on the Details
   * step so we can fire `PUT /api/v1/contacts/:id` only when it
   * actually changed — a view of the existing number shouldn't
   * generate a contact update.
   */
  initialCandidatePhone?: PhoneValue;
  /**
   * Resolved by the bulk-self-schedule prerequisite overlay (interview
   * plan picker + candidate categorisation). Undefined for every other
   * mode and for bulk while the overlay is still mounted — the wizard
   * shell gates rendering on its presence.
   */
  bulkSelfScheduleContext?: BulkSelfScheduleContextIds;
  /** Platform-specific adhoc entry point (today: HackerRank). Threaded
   *  to `useWizardInitialValues` so the location field seeds correctly. */
  interviewPlatform?: 'hackerrank';
  /** See `ScheduleInterviewProps.wouldStopAutomation`. */
  wouldStopAutomation?: (_nextScorecardRequired: boolean) => boolean;
  /** See `ScheduleInterviewProps.onScorecardSaved`. */
  onScorecardSaved?: (_nextScorecardRequired: boolean) => void | Promise<void>;
  onClose?: () => void;
  onSuccess?: () => void;
}

export function useScheduleInterview({
  mode,
  apiReference,
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  interviewId,
  scheduleId,
  initialCandidatePhone,
  bulkSelfScheduleContext,
  interviewPlatform,
  wouldStopAutomation,
  onScorecardSaved,
  onClose,
  onSuccess,
}: UseScheduleInterviewOptions) {
  const isEdit = apiReference === 'edit';
  const isSelfSchedule = isSelfScheduleMode(mode);

  // Edit is a flat experience — flip stepper configs to tabs so users
  // can jump directly between steps without enforced progression.
  const baseConfig: ScheduleModeConfig = MODE_CONFIGS[mode];
  const config: ScheduleModeConfig =
    isEdit && baseConfig.navigationStyle === 'stepper'
      ? { ...baseConfig, navigationStyle: 'tabs' }
      : baseConfig;

  // ---- Pre-fill: all three paths in one hook ----
  const {
    payloadRef,
    prefillVersion,
    interviewPlan,
    currentUserZoomStatus,
    planZoomHostType,
    planNoteTakerMandatory,
    meetingRoomsEnabled,
    showCreateLoader,
    showEditLoader,
    showEditError,
  } = useWizardInitialValues({
    mode,
    apiReference,
    stageId,
    stageActivityId,
    candidateId,
    interviewId,
    scheduleId,
    interviewPlatform,
  });

  // ---- Per-step form refs ----
  const { stepRefsMap, getStepRef, stepRefs } = useStepRefs(config.steps);

  // Snapshot the scorecard's original required/optional value once on edit
  // hydration (same `prefillVersion > 0` trigger `useEditConfirmation` uses) so
  // we can tell whether THIS edit actually toggled it — not just its value at
  // save time.
  const originalScorecardRequired = useRef<boolean | null>(null);
  const scorecardSnapshotCaptured = useRef(false);
  useEffect(() => {
    if (prefillVersion > 0 && !scorecardSnapshotCaptured.current) {
      scorecardSnapshotCaptured.current = true;
      originalScorecardRequired.current = Boolean(
        (payloadRef.current?.scorecard as { enabled?: boolean } | undefined)
          ?.enabled
      );
    }
  }, [prefillVersion, payloadRef]);

  // True only when this edit turned the scorecard from required → optional AND a
  // downstream automated activity is gated on its submission. Drives both the
  // confirm dialog (before save) and the automation cancel (after save), so they
  // stay in lockstep: an unchanged (or newly-required) scorecard does neither.
  const shouldStopAutomation = useCallback(() => {
    if (!isEdit) return false;
    const nextScorecardRequired = Boolean(
      (payloadRef.current?.scorecard as { enabled?: boolean } | undefined)
        ?.enabled
    );
    const turnedOptional =
      originalScorecardRequired.current === true &&
      nextScorecardRequired === false;
    return turnedOptional && Boolean(wouldStopAutomation?.(false));
  }, [isEdit, wouldStopAutomation, payloadRef]);

  // After a successful edit, stop the candidate's stage automation when this
  // edit turned the scorecard optional (handled inside `onScorecardSaved`). Runs
  // before the caller's own `onSuccess` refetch so a single refetch reflects
  // both.
  const handleSaveSuccess = useCallback(() => {
    if (shouldStopAutomation()) {
      void onScorecardSaved?.(false);
    }
    onSuccess?.();
  }, [shouldStopAutomation, onScorecardSaved, onSuccess]);

  // ---- Save logic (all four mutation paths) ----
  const { handleComplete, isSaving } = useScheduleSave({
    mode,
    isEdit,
    isSelfSchedule,
    applicationId,
    candidateId,
    jobId,
    stageId,
    stageActivityId,
    interviewId,
    scheduleId,
    initialCandidatePhone,
    bulkSelfScheduleContext,
    onClose,
    onSuccess: handleSaveSuccess,
  });

  // ---- Change-tracking confirmation (direct edits only) ----
  const {
    requestSave,
    confirmDialog,
    cancelDialog,
    dialogVariant,
    isDialogOpen,
    dialogFlags,
    dialogInterviewers,
  } = useEditConfirmation({
    payloadRef,
    prefillVersion,
    isEdit,
    isSelfSchedule,
  });

  // ---- Stop-automation confirm (scorecard edit that strands automation) ----
  // Chains ahead of `useEditConfirmation`'s invite/email gate: this asks first,
  // then the save proceeds through the existing confirmation flow.
  const { guardSave, confirmStop, cancelStop, isStopDialogOpen } =
    useScorecardStopConfirm({ shouldConfirmSave: shouldStopAutomation });

  // ---- Tab items ----
  const tabItems = useMemo(
    (): TabItem[] =>
      config.steps.map((step) => ({ label: step.label, value: step.key })),
    [config.steps]
  );

  // ---- Step validation orchestration ----
  const finalActionLabel = isEdit
    ? SCHEDULE_INTERVIEW_COPY.footer.saveInterview
    : SCHEDULE_INTERVIEW_COPY.footer.scheduleInterview;

  const onComplete = useCallback(
    async (payload: ScheduleInterviewPayload) => {
      guardSave(() =>
        requestSave((flags) => void handleComplete(payload, flags))
      );
    },
    [guardSave, requestSave, handleComplete]
  );

  const stepper = useStepValidation<ScheduleInterviewPayload>({
    tabItems,
    payloadRef,
    stepRefs,
    onComplete,
    finalActionLabel,
    nextPrefix: 'Next:',
    backPrefix: 'Back:',
    // Create steps through with Back/Next; edit is flat tab navigation
    // (primary is a direct "Save interview"), so suppress the stepping
    // Back button there — tabs handle movement.
    enableBack: !isEdit,
    wizardCompleteMode: 'validateAllSteps',
    activateFirstInvalidStepOnWizardComplete: true,
  });

  // ---- "Schedule interview now" secondary CTA ----
  // Validates every step in order, syncs to payload, then submits.
  // Kept here because it composes stepRefsMap + stepper.onStepChange
  // which are both available only at this orchestration level.
  const scheduleNow = useCallback(async () => {
    for (const step of config.steps) {
      const stepApi = stepRefsMap.current[step.key]?.current;
      if (!stepApi) continue;
      const ok = await stepApi.validate();
      if (!ok) {
        stepper.onStepChange(step.key);
        return;
      }
      stepApi.syncToPayload();
    }
    guardSave(() =>
      requestSave((flags) => void handleComplete(payloadRef.current, flags))
    );
  }, [
    config.steps,
    guardSave,
    handleComplete,
    payloadRef,
    requestSave,
    stepRefsMap,
    stepper,
  ]);

  return {
    config,
    payloadRef,
    getStepRef,
    isSaving,
    scheduleNow,
    prefillVersion,
    interviewPlan,
    currentUserZoomStatus,
    planZoomHostType,
    planNoteTakerMandatory,
    meetingRoomsEnabled,
    showEditLoader,
    showEditError,
    showCreateLoader,
    // Confirmation dialog state (for direct interview edits)
    dialogVariant,
    isDialogOpen,
    dialogFlags,
    dialogInterviewers,
    confirmDialog,
    cancelDialog,
    // Stop-automation confirm (scorecard edit that strands automation)
    isStopDialogOpen,
    confirmStop,
    cancelStop,
    // From useStepValidation
    tabItems: stepper.tabItems,
    activeStep: stepper.activeStep,
    isBusy: stepper.isBusy,
    onStepChange: stepper.onStepChange,
    goNextOrComplete: stepper.goNextOrComplete,
    goBack: stepper.goBack,
    canGoBack: stepper.canGoBack,
    primaryButtonLabel: stepper.primaryButtonLabel,
    backButtonLabel: stepper.backButtonLabel,
    /** Last step key — callers use this to hide the "Update interview"
     *  secondary CTA on the final step (primary CTA already saves there). */
    lastStepKey: config.steps[config.steps.length - 1]?.key,
  };
}
